import { motion, useScroll, useTransform, useMotionValue, useSpring, useMotionValueEvent } from "framer-motion";
import { useRef, useState, useEffect } from "react";

const Merchandise = () => {
  const heroRef = useRef<HTMLElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  // Mouse position tracking
  const mouseX = useMotionValue(50);
  const mouseY = useMotionValue(50);
  const [isHovering, setIsHovering] = useState(false);
  
  // Smooth spring animation for cursor position
  const smoothX = useSpring(mouseX, { stiffness: 100, damping: 30 });
  const smoothY = useSpring(mouseY, { stiffness: 100, damping: 30 });
  
  // Reveal radius (in percentage of viewport)
  const revealRadius = 25; // 25% of viewport width

  // Scroll-based zoom effect
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  // Zoom in effect - scales from 1 to 1.5
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.5]);
  
  // Title zoom effect - more pronounced zoom
  const titleScale = useTransform(scrollYProgress, [0, 1], [1, 2]);
  
  // Fade out as zooming in
  const opacity = useTransform(scrollYProgress, [0, 0.8, 1], [1, 0.3, 0]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.2, 0]);

  // Blur effect for depth
  const blur = useTransform(scrollYProgress, [0, 1], [0, 10]);
  const filter = useTransform(blur, (value) => `blur(${value}px)`);
  
  // Mask string state - start with empty mask (no reveal)
  const [maskString, setMaskString] = useState(
    `radial-gradient(circle ${revealRadius}vw at 50% 50%, transparent 0%, transparent 100%)`
  );
  
  // Update mask string when smoothX or smoothY changes
  useMotionValueEvent(smoothX, "change", (x) => {
    const y = smoothY.get();
    setMaskString(
      `radial-gradient(circle ${revealRadius}vw at ${x}% ${y}%, black 0%, transparent 100%)`
    );
  });
  
  useMotionValueEvent(smoothY, "change", (y) => {
    const x = smoothX.get();
    setMaskString(
      `radial-gradient(circle ${revealRadius}vw at ${x}% ${y}%, black 0%, transparent 100%)`
    );
  });
  
  // Track if initial animation has completed
  const [hasAnimated, setHasAnimated] = useState(false);
  
  // Initial animation on page load - simulate cursor movement (hover effect only)
  useEffect(() => {
    // Create an animation that moves the reveal effect across the screen
    const animateReveal = async () => {
      // Wait a brief moment before starting
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Enable hover state to show the image during animation
      setIsHovering(true);
      
      // Start from top-left
      mouseX.set(20);
      mouseY.set(20);
      
      // Animate to center-right
      await new Promise(resolve => {
        const startX = 20;
        const endX = 70;
        const startY = 20;
        const endY = 50;
        const duration = 3000; // 3 seconds
        const startTime = performance.now();
        
        const animate = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Ease in-out curve
          const easeProgress = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;
          
          const x = startX + (endX - startX) * easeProgress;
          const y = startY + (endY - startY) * easeProgress;
          
          mouseX.set(x);
          mouseY.set(y);
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            resolve(undefined);
          }
        };
        
        requestAnimationFrame(animate);
      });
      
      // Brief pause to show the reveal, then fade out
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Fade out the image
      setIsHovering(false);
      
      // Reset to center for normal hover behavior
      mouseX.set(50);
      mouseY.set(50);
      
      // Mark animation as complete
      setHasAnimated(true);
    };
    
    animateReveal();
  }, []); // Only run once on mount
  
  // Handle mouse movement
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const rect = section.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      
      mouseX.set(x);
      mouseY.set(y);
      setIsHovering(true);
    };
    
    const handleMouseLeave = () => {
      setIsHovering(false);
    };
    
    section.addEventListener('mousemove', handleMouseMove);
    section.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      section.removeEventListener('mousemove', handleMouseMove);
      section.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [mouseX, mouseY]);

  return (
    <main className="relative min-h-screen">
      {/* 🟠 HERO SECTION */}
      <motion.section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ paddingTop: '88px' }}
      >
        {/* Dark Background - Always visible */}
        <motion.div
          className="absolute inset-0 z-0"
          style={{
            backgroundColor: "#000000",
          }}
        />
        
        {/* Image Background with cursor reveal effect */}
        <motion.div
          ref={sectionRef}
          className="absolute inset-0 z-0 cursor-none"
          style={{
            scale,
            opacity,
            filter,
          }}
        >
          <img
            src="/assets/kyrmerchandise.png"
            alt="Merchandise Background"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            style={{
              objectFit: "cover",
              width: "100%",
              height: "100%",
              maskImage: maskString,
              WebkitMaskImage: maskString,
              opacity: isHovering ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />
        </motion.div>

        {/* Hero Content - Centered */}
        <div className="relative z-10 max-w-6xl mx-auto px-8 md:px-12 lg:px-16 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-6 leading-tight"
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(32px, 6vw, 72px)",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#FFFFFF",
              textShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
              fontStyle: "italic",
              scale: titleScale,
              opacity: titleOpacity,
            }}
          >
            <motion.span
              style={{
                display: "inline-block",
                transform: "skewX(-5deg)",
              }}
            >
              <span 
                className="inline-block relative"
                style={{ color: "#E67E22" }}
              >
                Coming Soon
                {/* Animated Underline */}
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                  style={{ backgroundColor: "#E67E22" }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                />
              </span>
            </motion.span>
          </motion.h1>
        </div>
      </motion.section>
    </main>
  );
};

export default Merchandise;

